import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import contentFallback from "../../data/content.json";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, textareaClass, saveCmsDoc } from "../cmsApi.jsx";
import {
  COLOR_FIELDS,
  LOGO_FIELDS,
  identityToSave,
  isValidHex,
  mergeIdentity,
} from "../../lib/identityAgdysa";

export default function CmsIdentidadEditor() {
  const fallback = useMemo(() => mergeIdentity(null, contentFallback.logos), []);
  const { data, loading, source } = useCmsDoc("site", "identity", fallback);
  const [form, setForm] = useState(null);
  const [iconRows, setIconRows] = useState([]);
  const [restriccionesText, setRestriccionesText] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!data) return;
    const next = mergeIdentity(data, contentFallback.logos);
    setForm(next);
    setIconRows(Object.entries(next.iconos || {}).map(([clave, url]) => ({ clave, url })));
    setRestriccionesText((next.proteccion?.restricciones || []).join("\n"));
  }, [data]);

  const hasInvalidHex = useMemo(
    () => (form ? COLOR_FIELDS.some((f) => !isValidHex(form.colores[f.key]?.hex)) : false),
    [form]
  );

  if (loading || !form) {
    return <p className="text-sm text-brand-dark/50">Cargando Identidad…</p>;
  }

  const patchLogo = (key, patch) =>
    setForm((f) => ({ ...f, logos: { ...f.logos, [key]: { ...f.logos[key], ...patch } } }));

  const patchColor = (key, patch) =>
    setForm((f) => ({ ...f, colores: { ...f.colores, [key]: { ...f.colores[key], ...patch } } }));

  const patchTipo = (which, patch) =>
    setForm((f) => ({
      ...f,
      tipografia: { ...f.tipografia, [which]: { ...f.tipografia[which], ...patch } },
    }));

  const onSave = async () => {
    if (hasInvalidHex) {
      setError("Hay colores con formato inválido. Use #RRGGBB.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = identityToSave(form, iconRows, restriccionesText);
      await saveCmsDoc("site", "identity", payload);
      setMessage("Identidad guardada. Recargue el sitio público para ver logos y colores.");
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-brand-dark/55">
        Marca del website (AGDYSA + LABSYSO). No incluye locución ni pantallas TV de SIAC.
        Fuente: {source}. El catálogo de iconos de contenido sigue en{" "}
        <Link to="/admin/icons" className="font-bold text-brand-primary underline">
          Iconos
        </Link>
        .
      </p>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Identificación</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Nombre oficial">
            <input
              className={inputClass}
              value={form.nombreOficial}
              onChange={(e) => setForm((f) => ({ ...f, nombreOficial: e.target.value }))}
            />
          </Field>
          <Field label="Acrónimo">
            <input
              className={inputClass}
              value={form.acronimo}
              onChange={(e) => setForm((f) => ({ ...f, acronimo: e.target.value }))}
            />
          </Field>
          <Field label="Eslogan">
            <input
              className={inputClass}
              value={form.eslogan}
              onChange={(e) => setForm((f) => ({ ...f, eslogan: e.target.value }))}
            />
          </Field>
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Logos</h3>
        <p className="text-xs text-brand-dark/50">
          URL de imagen (CDN). Márgenes = espacio de respeto. La placa e invertir aplican al mostrar el logo
          (navbar, pie).
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {LOGO_FIELDS.map(({ key, label, hint }) => {
            const asset = form.logos[key];
            const fondoVisible = !!asset?.fondoVisible;
            const fondoColor = asset?.fondoColor || "#FFFFFF";
            return (
              <div key={key} className="rounded-2xl border border-brand-soft p-4 space-y-3">
                <div className="flex items-center gap-3">
                  {asset?.url ? (
                    <span
                      className="flex h-12 w-20 shrink-0 items-center justify-center rounded-xl border border-brand-soft"
                      style={{ backgroundColor: fondoVisible ? fondoColor : "transparent" }}
                    >
                      <img
                        src={asset.url}
                        alt={label}
                        className="h-full w-full object-contain"
                        style={{
                          paddingTop: asset.margenTopPx || 0,
                          paddingBottom: asset.margenBottomPx || 0,
                          paddingLeft: asset.margenLeftPx || 0,
                          paddingRight: asset.margenRightPx || 0,
                          filter: asset.invertir ? "invert(1)" : undefined,
                        }}
                      />
                    </span>
                  ) : (
                    <span className="flex h-12 w-20 shrink-0 items-center justify-center rounded-xl border border-dashed border-brand-soft text-[10px] text-brand-dark/40">
                      sin URL
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold">{label}</p>
                    <p className="text-[11px] text-brand-dark/45">{hint}</p>
                  </div>
                </div>
                <Field label="URL">
                  <input
                    className={inputClass}
                    placeholder="https://…"
                    value={asset?.url || ""}
                    onChange={(e) => patchLogo(key, { url: e.target.value })}
                  />
                </Field>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    ["margenTopPx", "Arriba"],
                    ["margenBottomPx", "Abajo"],
                    ["margenLeftPx", "Izq."],
                    ["margenRightPx", "Der."],
                  ].map(([side, sideLabel]) => (
                    <Field key={side} label={sideLabel}>
                      <input
                        type="number"
                        min={0}
                        className={inputClass}
                        value={asset?.[side] ?? 0}
                        onChange={(e) => patchLogo(key, { [side]: Number(e.target.value) || 0 })}
                      />
                    </Field>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <label className="inline-flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={fondoVisible}
                      onChange={(e) => patchLogo(key, { fondoVisible: e.target.checked })}
                    />
                    Placa de fondo
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    Color
                    <input
                      type="color"
                      className="h-7 w-10 rounded border border-brand-soft"
                      value={fondoColor}
                      disabled={!fondoVisible}
                      onChange={(e) => patchLogo(key, { fondoColor: e.target.value })}
                    />
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={!!asset?.invertir}
                      onChange={(e) => patchLogo(key, { invertir: e.target.checked })}
                    />
                    Invertir colores
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Colores</h3>
        <p className="text-xs text-brand-dark/50">
          Paleta del Manual de marca 2026. Se inyectan en el sitio al guardar (variables CSS).
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {COLOR_FIELDS.map(({ key, label, hint }) => {
            const token = form.colores[key];
            const valid = isValidHex(token?.hex);
            return (
              <div key={key} className="rounded-2xl border border-brand-soft p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span
                    className="h-8 w-8 shrink-0 rounded-lg border border-brand-soft"
                    style={{ backgroundColor: valid ? token.hex : "transparent" }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-widest text-brand-dark/50">{label}</p>
                    <input
                      className={`${inputClass} font-mono uppercase ${valid ? "" : "ring-2 ring-red-400"}`}
                      value={token?.hex || ""}
                      onChange={(e) => patchColor(key, { hex: e.target.value })}
                    />
                  </div>
                </div>
                <p className="text-[11px] text-brand-dark/45">{hint}</p>
                <input
                  className={inputClass}
                  placeholder="Pantone (opcional)"
                  value={token?.pantone || ""}
                  onChange={(e) => patchColor(key, { pantone: e.target.value })}
                />
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Tipografía</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            ["principal", "Principal (titulares)"],
            ["secundaria", "Secundaria (cuerpo)"],
          ].map(([which, title]) => (
            <div key={which} className="space-y-3">
              <p className="text-sm font-bold">{title}</p>
              <Field label="Familia">
                <input
                  className={inputClass}
                  value={form.tipografia[which].family}
                  onChange={(e) => patchTipo(which, { family: e.target.value })}
                />
              </Field>
              <Field label="URL Google Fonts">
                <input
                  className={inputClass}
                  value={form.tipografia[which].googleFontsUrl || ""}
                  onChange={(e) => patchTipo(which, { googleFontsUrl: e.target.value })}
                />
              </Field>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Iconos de marca (clave → URL)</h3>
            <p className="text-xs text-brand-dark/50 mt-1">
              Solo assets sueltos de identidad. El catálogo editorial está en Iconos.
            </p>
          </div>
          <button
            type="button"
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
            onClick={() => setIconRows((rows) => [...rows, { clave: "", url: "" }])}
          >
            + Icono
          </button>
        </div>
        {iconRows.length === 0 ? (
          <p className="text-xs text-brand-dark/40">Sin iconos personalizados en identidad.</p>
        ) : null}
        <div className="space-y-2">
          {iconRows.map((row, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2">
              <input
                className={`${inputClass} sm:w-40`}
                placeholder="clave"
                value={row.clave}
                onChange={(e) =>
                  setIconRows((rows) => rows.map((r, i) => (i === idx ? { ...r, clave: e.target.value } : r)))
                }
              />
              <input
                className={`${inputClass} flex-1 min-w-[12rem]`}
                placeholder="https://…/icono.svg"
                value={row.url}
                onChange={(e) =>
                  setIconRows((rows) => rows.map((r, i) => (i === idx ? { ...r, url: e.target.value } : r)))
                }
              />
              <button
                type="button"
                className="text-xs font-bold text-red-600"
                onClick={() => setIconRows((rows) => rows.filter((_, i) => i !== idx))}
              >
                Quitar
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Protección y restricciones</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Margen mínimo (%)">
            <input
              type="number"
              className={inputClass}
              value={form.proteccion.margenMinPct ?? 0}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  proteccion: { ...f.proteccion, margenMinPct: Number(e.target.value) || 0 },
                }))
              }
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Restricciones (una por línea)">
              <textarea
                className={textareaClass}
                value={restriccionesText}
                onChange={(e) => setRestriccionesText(e.target.value)}
              />
            </Field>
          </div>
        </div>
      </section>

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
